import { useState, useEffect } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useLocation, useSearch, Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
const HERO_BG = "/assets/hero-bg-Y8F6k5ayA9btVQcqwjUfpV.webp";

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const token = params.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const resetPasswordMutation = trpc.auth.resetPassword.useMutation();

  // If no token in URL, redirect to forgot password page
  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link. Please request a new one.");
      setLocation("/auth/forgot-password");
    }
  }, [token, setLocation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await resetPasswordMutation.mutateAsync({ token, newPassword });
      setSuccess(true);
    } catch (error: any) {
      const message = error?.message || "Failed to reset password. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) return null;

  return (
    /* flex column with a flex-1 section, so the footer is pinned to the
       bottom of the viewport rather than floating halfway up a short page. */
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader showPrimaryAction={false} />

      {/* ── HERO SECTION ── */}
      <section className="flex-1 relative overflow-hidden min-h-[calc(100vh-3.5rem)] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-background/55" />

        <div className="relative container py-16">
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-card rounded-lg border border-border shadow-[0_2px_8px_oklch(0.22_0.015_65/0.06)] p-8">
                {!success ? (
                  <>
                    <h1 className="font-['Fraunces'] text-2xl font-bold text-foreground mb-1">
                      Create New Password
                    </h1>
                    <p className="text-sm text-muted-foreground mb-6">
                      Choose a strong password for your account. It must be at least 6 characters.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-1.5">
                          New Password
                        </label>
                        <Input
                          id="newPassword"
                          type="password"
                          placeholder="At least 6 characters"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-background"
                          autoFocus
                        />
                      </div>

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1.5">
                          Confirm Password
                        </label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="Repeat your new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-background"
                        />
                        {confirmPassword && newPassword !== confirmPassword && (
                          <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Passwords do not match
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading || (!!confirmPassword && newPassword !== confirmPassword)}
                        className={`w-full ${BUTTON.primary}`}
                      >
                        {isLoading ? "Updating..." : (
                          <>
                            Update Password
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </form>

                    <p className="text-sm text-muted-foreground text-center mt-6">
                      <button
                        onClick={() => setLocation("/auth/login")}
                        className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back to sign in
                      </button>
                    </p>
                  </>
                ) : (
                  /* ── SUCCESS STATE ── */
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="text-center"
                  >
                    <div className="flex justify-center mb-4">
                      <CheckCircle className="w-12 h-12 text-[oklch(0.38_0.09_155)]" />
                    </div>
                    <h2 className="font-['Fraunces'] text-xl font-bold text-foreground mb-2">
                      Password Updated!
                    </h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      Your password has been successfully changed. You can now sign in with your new password.
                    </p>

                    <button
                      onClick={() => setLocation("/auth/login")}
                      className={BUTTON.primary}
                    >
                      Sign In Now
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
